import { useState, useCallback, useEffect, useMemo } from "react";
import { Icon } from "@elaniintc/goes-design-system";

import FullModal from "../../full-modal";
import { Timer, type TimerValues } from "./timer";

import Empty from "@/workflow_api/components/variables/empty";
import Variables from "@/workflow_api/components/variables";

import { useUIStore } from "@/workflow_api/store/uiStore";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";

import {
  useCreateNodeExpiration,
  useGetNode,
  useUpdateNode,
  useUpdateNodeExpiration,
  useDeleteNodeExpiration,
  useCreateNodeTimeExtension,
  useDeleteNodeTimeExtension,
  useUpdateNodeTimeExtension,
} from "@/workflow_api/services/nodes.api";

import ThreeColumnLayout from "@/components/common/three-column-layout";
import { ConfirmationModalBase } from "@/components/common/modals";
import type { NodeData, RefName } from "@/workflow_api/types/workflow";

// ================== Interfaces & Types ==================
interface Props {
  initialTitle?: string;
}

export type NodeExpirationConfig = {
  limit_unit: "minute" | "hour" | "day" | "month" | "year";
  limit_amount: number;
  days: ("mon" | "tue" | "wed" | "thu" | "fri")[];
};

export type NodeExtensionConfig = {
  extensionAmount: number | null;
  extensionUnit: "minute" | "hour" | "day" | "month" | "year" | undefined;
  leaderApproval: boolean;
};

// ================== Component ==================
export const TimerDetails: React.FC<Props> = ({
  initialTitle = "Temporizador",
}) => {
  // ---------- Stores ----------
  const {
    selectedNodeId,
    timerDetailsOpen,
    setNodeDetailsOpen,
    setTimerDetailsOpen,
    setSelectedNodeId,
  } = useUIStore();

  // --------Get node mutation -----------------
  const { mutateAsync: getNode, data: dataNode, isPending } = useGetNode();

  const { getNodeById, flowId } = useWorkflowStore();
  const node = getNodeById(selectedNodeId || "");
  const nodeData = node?.data as unknown as NodeData;

  // ---------- States ----------
  const [title, setTitle] = useState<string>(
    dataNode?.expiration?.display_name || initialTitle,
  );
  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );

  const [timerData, setTimerData] = useState<NodeExpirationConfig>({
    limit_unit: "hour",
    limit_amount: 0,
    days: [],
  });
  const [extensionData, setExtensionData] =
    useState<null | NodeExtensionConfig>(null);

  const [hasTimerFromChild, setHasTimerFromChild] = useState<boolean>(false);

  // estado de guardado para bloquear el botón
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // ---------- Queries & Mutations ----------

  const { mutateAsync: updateNode } = useUpdateNode(flowId);
  const { mutateAsync: createExpiration } = useCreateNodeExpiration(node?.id);
  const { mutateAsync: updateExpiration } = useUpdateNodeExpiration(flowId);
  const { mutateAsync: deleteExpiration } = useDeleteNodeExpiration(flowId);

  // Time extension queries
  const { mutateAsync: createExtension } = useCreateNodeTimeExtension();
  const { mutateAsync: updateExtension } = useUpdateNodeTimeExtension();
  const { mutateAsync: deleteExtension } = useDeleteNodeTimeExtension();

  useEffect(() => {
    if (node?.id) {
      void getNode(node.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node?.id]);

  // ---------- Derivados / Validaciones ----------

  const isDeleteOperation = useMemo(
    () => Boolean(dataNode?.expiration?.id) && hasTimerFromChild === false,
    [dataNode?.expiration?.id, hasTimerFromChild],
  );

  const isFormComplete = useMemo(() => {
    const unitOk = Boolean(timerData.limit_unit);
    const amountOk =
      typeof timerData.limit_amount === "number" && timerData.limit_amount > 0;
    const daysOk = Array.isArray(timerData.days) && timerData.days.length > 0;

    if (!extensionData) return unitOk && amountOk && daysOk;
    const extAmountOk =
      extensionData.extensionAmount !== null &&
      typeof extensionData.extensionAmount === "number" &&
      extensionData.extensionAmount > 0;
    const extUnitOk = Boolean(extensionData.extensionUnit);

    return unitOk && amountOk && daysOk && extAmountOk && extUnitOk;
  }, [timerData, extensionData]);

  // isSaveValid: habilita guardar si no está guardando y
  const isSaveValid = !isSaving && (isDeleteOperation || isFormComplete);

  // ---------- Callbacks ----------
  const handleTimerChange = useCallback((values: NodeExpirationConfig) => {
    setTimerData((prev) => {
      const sameUnit = prev.limit_unit === values.limit_unit;
      const sameAmount = prev.limit_amount === values.limit_amount;
      const sameDays =
        prev.days.length === values.days.length &&
        prev.days.every((d, i) => d === values.days[i]);

      return sameUnit && sameAmount && sameDays ? prev : values;
    });
  }, []);

  const handleTimeExtensionChange = useCallback(
    (value: NodeExtensionConfig) => {
      setExtensionData((prev) => {
        if (prev === null) return value;
        if (value === null) return null;
        const sameAmount = prev.extensionAmount === value.extensionAmount;
        const sameUnit = prev.extensionUnit === value.extensionUnit;
        const sameLeaderApproval = prev.leaderApproval === value.leaderApproval;

        return sameAmount && sameUnit && sameLeaderApproval ? prev : value;
      });
    },
    [],
  );

  const handleSave = useCallback(async () => {
    if (!node?.id) return;

    try {
      setIsSaving(true);

      // ====== Eliminar (permitido aunque falten campos) ======
      if (dataNode?.expiration?.id && hasTimerFromChild === false) {
        await deleteExpiration(dataNode.expiration.id);

        setNodeDetailsOpen(false);
        setTimerDetailsOpen(false);
        setSelectedNodeId();
        setModalState("idle");

        await updateNode;

        await getNode(node.id);
        setIsSaving(false);
        return;
      }

      // ====== Guardar / Editar: bloquear si faltan campos ======
      if (!isFormComplete) {
        setIsSaving(false);
        return;
      }

      const payload = {
        display_name: title,
        limit_unit: timerData.limit_unit,
        limit_amount: timerData.limit_amount,
        node_id: node.id,
        available_days: timerData.days,
      };
      let expirationId = dataNode?.expiration?.id || "";

      if (dataNode?.expiration?.id) {
        await updateExpiration({
          id: dataNode.expiration.id,
          data: payload,
        });
      } else {
        const expiration = await createExpiration({ expiration: payload });
        expirationId = expiration.data.id;
        await updateNode({
          node_id: node.id,
          node: {
            flow_id: flowId,
            ref_name: nodeData.type as RefName,
            display_name: nodeData.displayName,
            order: nodeData.order,
            position_x: node.position.x,
            position_y: node.position.y,
            type: nodeData.node_type,
            connections: {
              ...nodeData.connections,
              expired: {
                display_name: "Expirado",
                target_node: null,
              },
            },
          },
        });
      }

      if (!extensionData && dataNode?.expiration?.node_time_extension) {
        await deleteExtension(dataNode.expiration.node_time_extension.id);
      }

      if (extensionData && !dataNode?.expiration?.node_time_extension) {
        await createExtension({
          availableDays: timerData.days,
          extensionAmount: extensionData.extensionAmount!,
          extensionUnit: extensionData.extensionUnit!,
          node_expiration_id: expirationId,
          leaderRequired: extensionData?.leaderApproval || false,
        });
      }

      if (extensionData && dataNode?.expiration?.node_time_extension) {
        await updateExtension({
          id: dataNode?.expiration?.node_time_extension?.id || "",
          availableDays: timerData.days,
          extensionAmount: extensionData.extensionAmount!,
          extensionUnit: extensionData.extensionUnit!,
          leaderRequired: extensionData.leaderApproval,
        });
      }

      setNodeDetailsOpen(false);
      setTimerDetailsOpen(false);
      setSelectedNodeId();
      setModalState("idle");

      await getNode(node.id);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  }, [
    title,
    timerData,
    node,
    flowId,
    nodeData,
    dataNode?.expiration,
    hasTimerFromChild,
    deleteExpiration,
    updateExpiration,
    createExpiration,
    updateNode,
    getNode,
    setNodeDetailsOpen,
    setSelectedNodeId,
    setTimerDetailsOpen,
    isFormComplete,
    createExtension,
    extensionData,
    updateExtension,
    deleteExtension,
  ]);

  // ---------- Memo: initialValues para Timer ----------
  const memoInitialValues:
    | {
        expiration: Partial<NodeExpirationConfig>;
        extension: Partial<NodeExtensionConfig> | null;
      }
    | undefined = useMemo(() => {
    if (!dataNode?.expiration) return undefined;
    return {
      expiration: {
        limit_unit: dataNode.expiration.limit_unit as TimerValues["limit_unit"],
        limit_amount: dataNode.expiration.limit_amount,
        days: dataNode.expiration.available_days as TimerValues["days"],
      },
      extension: dataNode.expiration.node_time_extension
        ? {
            extensionAmount:
              dataNode.expiration.node_time_extension.extension_amount,
            extensionUnit: dataNode.expiration.node_time_extension
              .extension_unit as TimerValues["limit_unit"] | undefined,
            leaderApproval:
              dataNode.expiration.node_time_extension.leader_required || false,
          }
        : null,
    };
  }, [dataNode?.expiration]);

  const timerKey = useMemo(
    () =>
      dataNode?.expiration?.id
        ? `exp-${dataNode.expiration.id}`
        : `node-${node?.id ?? "none"}`,
    [dataNode?.expiration?.id, node?.id],
  );

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={false}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setTimerDetailsOpen(false);
          setSelectedNodeId();
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />

      <FullModal
        isOpen={timerDetailsOpen}
        onClose={() => setModalState("close-warning")}
        isSaveValid={isSaveValid}
        onSave={handleSave}
      >
        <ThreeColumnLayout
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
          }}
          centerHeader={{
            icon: "alarm-clock",
            title: title,
            color: "neutral",
            editable: false,
            onTitleChange: (newTitle: string) => setTitle(newTitle),
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContent={
            <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
              <Variables sourceNodeId={selectedNodeId!} />
            </div>
          }
          centerContent={
            <>
              {isPending ? (
                <div className="w-full flex items-center justify-center min-h-[300px]">
                  <Icon
                    name="spinner-third"
                    className="animate-spin text-on-disabled"
                    type="far"
                  />
                </div>
              ) : (
                <Timer
                  key={timerKey}
                  initialValues={memoInitialValues}
                  onChange={(values, expiration) => {
                    handleTimerChange(values as NodeExpirationConfig);
                    handleTimeExtensionChange(
                      expiration as NodeExtensionConfig,
                    );
                  }}
                  onHasTimerChange={(has) => setHasTimerFromChild(has)}
                />
              )}
            </>
          }
          rightContent={<Empty />}
        />
      </FullModal>
    </>
  );
};
